import z from 'zod'
import {
  firestoreWriteTimestamp,
  nullSafeFirestoreTimestamp,
  nullSafeOptionalBoolean,
  nullSafeOptionalFirestoreTimestamp,
  nullSafeOptionalNumber,
  nullSafeOptionalString,
} from '../shared/schema'
import {
  AssignmentGroupingType,
  AssignmentState,
  AssignmentType,
  CompletionMetric,
} from './types'

export {
  AssignmentGroupingType,
  AssignmentState,
  AssignmentType,
  CompletionMetric,
}

const ltiGradingLineItemOptions = z.object({
  id: z.string(),
  label: z.string(),
  scoreMaximum: z.number(),
})

/**
 * Per-student date overrides, mirroring the assignment-level date triple.
 * Canvas "Assign To" rows carry Available from (unlockAt), Due Date (dueAt),
 * and Until (lockAt) per student — assignedAt/overdueAt/expiresAt respectively.
 * A student's effective deadline is max(overdueAt, expiresAt) within the entry,
 * and the entry is authoritative for that student — it replaces the assignment
 * window entirely (no late-submission period applies to individual deadlines),
 * so an entry end before the assignment due date would genuinely shorten that
 * student's window. The extend-only intent is an invariant of the two writers
 * (the in-app dialog validates due > assignment due; Canvas sync only writes
 * when the student's end extends past expiresAt) — it is NOT enforced by this
 * schema, the model getters, or firestore.rules, which all treat the stored
 * entry as authoritative.
 */
export const individualDeadlineSchema = z.object({
  assignedAt: nullSafeOptionalFirestoreTimestamp(),
  overdueAt: nullSafeOptionalFirestoreTimestamp(),
  expiresAt: nullSafeOptionalFirestoreTimestamp(),
})

export type IndividualDeadline = z.infer<typeof individualDeadlineSchema>

const individualDeadlineWriteSchema = z.object({
  assignedAt: firestoreWriteTimestamp.optional(),
  overdueAt: firestoreWriteTimestamp.optional(),
  expiresAt: firestoreWriteTimestamp.optional(),
})

// this is the dart definition
// required int assignedAt,
// required AssignmentType assignmentType,
// required int expiresAt,
// required AssignmentGroupingType groupingType, default: AssignmentGroupingType.none
// required String sectionId,
// required String slideDeckId,
// int? groupingSize,
// int? updatedAt,
export const schema = z.object({
  assignedAt: nullSafeOptionalFirestoreTimestamp(),
  assignmentType: z.nativeEnum(AssignmentType),
  assignmentGradingScalars: z
    .object({
      attendanceScore: z.number().optional().default(0),
      quizScore: z.number().optional().default(0),
      participationScore: z.number().optional().default(0),
      totalRubricScore: z.number().optional().default(0),
    })
    .optional(),
  // Assignment-level gate for custom rubric grading. Set true when custom
  // rubrics are configured; absent/false on historical assignments (which then
  // fall back to SlideRubric + default scoring). See AssignmentRubric.
  usesCustomRubrics: nullSafeOptionalBoolean(false),
  assignmentOverdueAt: nullSafeOptionalFirestoreTimestamp(),
  customGroupingSelected: nullSafeOptionalBoolean(false),
  expiresAt: nullSafeOptionalFirestoreTimestamp(),
  forbidStudentGroupCreation: nullSafeOptionalBoolean(false),
  forbidStudentGroupScheduling: nullSafeOptionalBoolean(false),
  forbidStudentGroupSelection: nullSafeOptionalBoolean(false),
  /** When present, overrides forbidStudentGroupSelection for joining. NO default — fallback relies on undefined. */
  forbidStudentGroupJoining: nullSafeOptionalBoolean(),
  /** When present, overrides forbidStudentGroupSelection for leaving. NO default — fallback relies on undefined. */
  forbidStudentGroupLeaving: nullSafeOptionalBoolean(),
  forceScheduledAt: nullSafeOptionalFirestoreTimestamp(),
  groupingType: z.preprocess((val) => {
    return val ?? AssignmentGroupingType.manual
  }, z.nativeEnum(AssignmentGroupingType)),
  assignmentState: z.preprocess((val) => {
    return val ?? AssignmentState.active // default to active
  }, z.nativeEnum(AssignmentState)),
  sectionId: z.string(),
  slideDeckId: z.string(),
  /** @deprecated use the desiredGroupSize getter instead */
  groupingSize: nullSafeOptionalNumber(),
  /** @deprecated use the maxGroupSize getter instead */
  groupingSizeMaximum: nullSafeOptionalNumber(),
  groupingSizeMinimum: nullSafeOptionalNumber(),
  catalogId: z.string().nullish(),
  updatedAt: nullSafeFirestoreTimestamp(),
  ltiType: z.string().optional(),
  ltiMaxScore: z.number().optional(),
  ltiCourseId: z.string().optional(),
  ltiAssignmentId: z.string().optional(),
  ltiDomain: z.string().optional(),
  ltiLineItemMissing: z.boolean().optional(),
  peerReviewExcludesSelfReview: nullSafeOptionalBoolean(false),
  peerReviewsHiddenFromReviewees: nullSafeOptionalBoolean(false),
  ltiGradingLineItemId: nullSafeOptionalString(),
  ltiGradingLineItemOptions: z.array(ltiGradingLineItemOptions).optional(),
  /** allow participants to view final score calculation in meeting results view */
  showFinalGradeToParticipants: nullSafeOptionalBoolean(false),
  /**
   * How the Completion criterion is scored. Absent = legacy timeInSession.
   * Stored top-level, NOT inside assignmentGradingScalars (that object is
   * validated to sum to 1 and bulk-converted int<->float; a non-weight field
   * there corrupts the total).
   */
  completionMetric: z.nativeEnum(CompletionMetric).optional(),
  /** per-student date overrides, keyed by Breakout user id */
  individualDeadlines: z
    .record(z.string(), individualDeadlineSchema)
    .optional(),
  /** denormalized max effective end across individualDeadlines, for cron queries */
  latestIndividualDeadline: nullSafeOptionalFirestoreTimestamp(),
})

export const writeSchema = schema.extend({
  assignedAt: firestoreWriteTimestamp,
  assignmentOverdueAt: firestoreWriteTimestamp.optional(),
  expiresAt: firestoreWriteTimestamp,
  updatedAt: firestoreWriteTimestamp,
  individualDeadlines: z
    .record(z.string(), individualDeadlineWriteSchema)
    .optional(),
  latestIndividualDeadline: firestoreWriteTimestamp.optional(),
})

export const draftWriteSchema = writeSchema.extend({
  assignedAt: firestoreWriteTimestamp.optional(),
  assignmentOverdueAt: firestoreWriteTimestamp.optional(),
  expiresAt: firestoreWriteTimestamp.optional(),
})

export const empty = Object.freeze(
  schema.parse({
    assignedAt: { seconds: 0, nanoseconds: 0 },
    assignmentType: AssignmentType.ManyGroups,
    expiresAt: { seconds: 0, nanoseconds: 0 },
    groupingType: AssignmentGroupingType.manual,
    sectionId: '',
    slideDeckId: '',
  })
)

type FirestoreSectionAssignmentInferredType = z.infer<typeof schema>

export type FirestoreSectionAssignmentWrite = z.infer<typeof writeSchema>

export interface FirestoreSectionAssignment extends FirestoreSectionAssignmentInferredType {}
